import { addDays, startOfDay } from 'date-fns'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Flex from 'typescript-styled-flex'
import DayCountText from '../../../components/FormBuilder/DatePicker/components/DayCountText'
import DatePickerDrawer from '../../../components/FormBuilder/DatePicker/DatePickerDrawer'
import DateRangePickerWidget from '../../../components/FormBuilder/DatePicker/DateRangePickerWidget'
import { DatePickerDrawerFooter } from '../../../components/FormBuilder/DatePicker/styled'
import Button from '../../../components/Template/Button'
import {
  CloseButton,
  ModalHeader,
  ModalHeaderLabel,
} from '../../../components/Template/Modal'
import SpinningIcon from '../../../components/Template/SpinningIcon'
import PrintModalWrapper from '../../../components/Calendar/PrintModal/PrintModalWrapper'
import { is } from '../../../components/MediaQuery'
import ModalFlexOverflow from '../../../components/Template/Modal/ModalFlexOverflow'
import useSize from '../../../hooks/useSize'
import { getDimensions } from '../../../utils/Canvas'
import clamp from '../../../utils/clamp'
import { dim } from '../Calendar/Calendar'
import PrintWrapperCalendar, { PRINT_DAYS } from './PrintCalendar'

function PrintModal({
  open,
  modalId,
  onClose,
  isCalendarLoading,
  summaryToggled,
  initialDate = new Date(),
}: IPrintModalProps & IPrintModalState & IPrintModalDispatch) {
  const [date, setDate] = useState(startOfDay(initialDate))
  const [loading, isLoading] = useState(false)
  const [t] = useTranslation()
  const calendarRef = useRef(null as HTMLDivElement)
  const printFrameRef = useRef(null as HTMLIFrameElement)

  const size = useSize('calendar')
  const isMobile = is.mobile()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dimensions = useMemo(() => getDimensions(dim, size, isMobile), [size])
  const fullWidth =
    dimensions.ROOM_WIDTH + dimensions.GRID_WIDTH * PRINT_DAYS - 1

  useEffect(
    () => setDate(startOfDay(initialDate)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  )

  function handleClose() {
    if (onClose) {
      onClose(modalId)
    }
  }

  function handleDateChange(date: Date) {
    setDate(startOfDay(date))
  }

  function printFrame() {
    if (open && printFrameRef.current) {
      printFrameRef.current.contentWindow.print()
    }
  }

  const flexItUp = (el: HTMLElement) => {
    const container = document.createElement('div')
    container.style.minWidth = '0'
    el.style.width = '100%'
    container.appendChild(el)

    return container
  }

  const chunkCanvases = async (
    rooms: HTMLImageElement,
    events: HTMLImageElement,
  ): Promise<HTMLDivElement[]> => {
    const CHUNK_COUNT = 15
    const PAGE_HEIGHT = CHUNK_COUNT * dimensions.GRID_HEIGHT
    const { SUMMARY_HEIGHT } = dimensions

    let y = 0
    const chunks = []
    const imageHeight = events.height
    // Total count is first page + number of immediate pages
    const chunkCount = clamp(Math.ceil(imageHeight / PAGE_HEIGHT), 0)
    for (let i = 0; i < chunkCount; i++) {
      let chunkHeight = PAGE_HEIGHT
      if (summaryToggled && i === chunkCount - 1) {
        chunkHeight += SUMMARY_HEIGHT
      }
      const [roomChunk, eventsChunk] = (await Promise.all(
        // eslint-disable-next-line no-loop-func
        [rooms, events].map(c => {
          const canvas = document.createElement('canvas')
          canvas.width = c.width
          canvas.height = chunkHeight
          canvas.getContext('2d').drawImage(c, 0, -1 * y)
          const uri = canvas.toDataURL()

          const img = document.createElement('img')
          img.style.minWidth = '0'
          img.src = uri

          return new Promise(resolve => {
            img.addEventListener('load', () => {
              resolve(img)
            })
          })
        }),
      )) as any
      const chunk = document.createElement('div')
      chunk.style.display = 'flex'
      chunk.appendChild(flexItUp(roomChunk))
      chunk.appendChild(flexItUp(eventsChunk))

      chunks.push(chunk)

      y += chunkHeight
    }

    return chunks
  }

  const prepareIframe = (iframeDocument: Document) => {
    const style = iframeDocument.createElement('style')
    style.innerHTML = '@media print {@page {margin: 8mm}}'
    iframeDocument.head.appendChild(style)
    iframeDocument.body.style.maxWidth = `${fullWidth}px`
    iframeDocument.body.innerHTML = '<div></div>'
  }

  async function getCanvasImagesFromContainer(): Promise<HTMLImageElement[]> {
    return Promise.all<HTMLImageElement>(
      ['HeaderCanvas', 'RoomCanvas', 'CalendarCanvas'].map(className => {
        const uri = (calendarRef.current
          .getElementsByClassName(className)
          .item(0) as HTMLCanvasElement).toDataURL()

        const img = document.createElement('img')
        img.src = uri

        return new Promise(resolve => {
          img.addEventListener('load', () => {
            resolve(img)
          })
        })
      }),
    )
  }

  async function handlePrint() {
    if (calendarRef.current) {
      isLoading(true)

      const iframeDocument = printFrameRef.current.contentDocument
      prepareIframe(iframeDocument)

      const domToImage = await import('../../../utils/domToImage')
      const image: HTMLImageElement = new Image()
      const imageData = await domToImage.toPng(
        calendarRef.current
          .getElementsByClassName('FilterBarContainer')
          .item(0),
        {
          height: 32,
          style: {
            border: 'none',
            display: 'block',
          },
          width: fullWidth,
        } as any,
      )

      image.src = imageData

      const datesContainer = document.createElement('div')
      image.setAttribute('crossOrigin', 'anonymous')
      image.style.width = '100%'
      datesContainer.appendChild(image)
      iframeDocument.body.appendChild(datesContainer)
      const [
        HeaderCanvas,
        RoomCanvas,
        CalendarCanvas,
      ] = await getCanvasImagesFromContainer()

      const container = document.createElement('div')
      container.style.display = 'flex'
      const FilterButton = calendarRef.current
        .getElementsByClassName('FilterButton')
        .item(0)

      const rules = window.getComputedStyle(FilterButton)
      const cssw: HTMLImageElement = new Image()
      const csswData = await domToImage.toPng(FilterButton, {
        height: parseInt(rules.height, 10),
        style: {
          border: 'none',
        },
        width: parseInt(rules.width, 10),
      } as any)
      cssw.src = csswData
      cssw.style.minWidth = '0'
      HeaderCanvas.style.minWidth = '0'
      container.appendChild(flexItUp(cssw))
      container.appendChild(flexItUp(HeaderCanvas))

      iframeDocument.body.appendChild(container)
      const chunkedEvents = await chunkCanvases(RoomCanvas, CalendarCanvas)
      chunkedEvents.forEach((d, i) => {
        if (i < chunkedEvents.length - 1) {
          d.style.pageBreakAfter = 'always'
        }
        iframeDocument.body.appendChild(d)
      })
      printFrame()

      isLoading(false)
    }
  }

  const PrintButton = () => (
    <Button
      onClick={handlePrint}
      style={{ overflow: 'hidden', width: '100%' }}
      primary={!loading && !isCalendarLoading}
      disabled={loading || isCalendarLoading}
    >
      {loading ? <SpinningIcon /> : t('print')}
    </Button>
  )

  return (
    <>
      {isMobile ? (
        <DatePickerDrawer
          open={open}
          onClose={handleClose}
          header={t('BBO_1250_print_calendar')}
        >
          <DateRangePickerWidget
            startDate={date}
            endDate={addDays(date, PRINT_DAYS - 1)}
            setStartDate={handleDateChange}
            setEndDate={null}
          />
          <DatePickerDrawerFooter>
            <DayCountText
              startDate={date}
              endDate={addDays(date, PRINT_DAYS - 1)}
              includeEnd={true}
            />
            <PrintButton />
          </DatePickerDrawerFooter>
        </DatePickerDrawer>
      ) : (
        <ModalFlexOverflow open={open} onClose={handleClose}>
          <PrintModalWrapper>
            <ModalHeader>
              <Flex
                style={{
                  fontWeight: 600,
                  marginBottom: '11px',
                  maxHeight: '20px',
                  width: '100%',
                }}
                justifyStart={true}
                alignCenter={true}
                row={true}
              >
                <ModalHeaderLabel style={{ width: '100%' }}>
                  {t('BBO_1250_print_calendar')}
                </ModalHeaderLabel>
                <Flex
                  style={{
                    marginRight: '-14px',
                    minWidth: '57px',
                    overflow: 'hidden',
                    width: '20%',
                  }}
                  justifyEnd={true}
                >
                  <CloseButton onClose={handleClose} />
                </Flex>
              </Flex>
            </ModalHeader>
            <DateRangePickerWidget
              startDate={date}
              endDate={addDays(date, PRINT_DAYS - 1)}
              setStartDate={handleDateChange}
              setEndDate={null}
            />
            <Flex>
              <PrintButton />
            </Flex>
          </PrintModalWrapper>
        </ModalFlexOverflow>
      )}
      {open && (
        <>
          <PrintWrapperCalendar
            calendarRef={calendarRef}
            startDate={date}
            size={size}
          />
          <iframe
            title={'printIframe'}
            ref={printFrameRef}
            style={{ display: 'none' }}
          />
        </>
      )}
    </>
  )
}

export default PrintModal

export interface IPrintModalProps {
  open?: boolean
  modalId?: boolean
}

export interface IPrintModalState {
  initialDate?: Date
  isCalendarLoading?: boolean
  summaryToggled?: boolean
}

export interface IPrintModalDispatch {
  onClose?: (id) => void
  onSubmit?: () => void
}
